/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: FeatureCategoryFormView.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Form view for creating and editing Feature Categories.

 🔧 Notes:
    - Refactored from monolithic 800-line file
    - Uses hooks for state management
    - Uses FeatureCodeShuttle component for feature selection
    - Maintains QuickSearchModal functionality

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Layers, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import { ModuleFormHeader } from '../../components/layouts';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { Label } from '../../components/ui/label';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { useFeatureCategoryData } from './hooks/useFeatureCategoryData';
import { useFeatureCategoryForm } from './hooks/useFeatureCategoryForm';
import { FeatureCodeShuttle } from './components/FeatureCodeShuttle';
import { deleteFeatureCategory, fetchFeatureCategoryById, fetchFeatureCategoriesForQuickList } from './api/featureCategoriesApi';
import { navigateToFeatureCategoryDuplicate } from './featureCategoriesActions';
import { FeatureCategory } from './types';

type FormFieldProps = {
  id: string;
  label: string;
  tooltip: string;
  children: ReactNode;
  required?: boolean;
};

const FormField = ({ id, label, tooltip, children, required }: FormFieldProps) => (
  <>
    <SimpleTooltip content={tooltip}>
      <Label htmlFor={id} className="text-left">
        {label}
        {required ? ' *' : ''}
      </Label>
    </SimpleTooltip>
    {children}
  </>
);

export default function FeatureCategoryFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { notifications, removeNotification, showSuccess, showError } = useNotifications();

  const mode = id === 'new' ? 'create' : 'edit';
  const isEditMode = mode === 'edit';

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListCategories, setQuickListCategories] = useState<FeatureCategory[]>([]);
  const [isLoadingQuickList, setIsLoadingQuickList] = useState(false);

  const {
    isLoading,
    formData: loadedFormData,
    selectedFeatureIds: loadedSelectedFeatureIds,
    availableFeatures,
  } = useFeatureCategoryData(id, duplicateId);

  const {
    formData,
    errors,
    isSaving,
    hasChanges,
    selectedFeatureIds,
    handleChange,
    moveToEnabled,
    moveToAvailable,
    handleSubmit,
    handleCancel,
  } = useFeatureCategoryForm(
    loadedFormData,
    loadedSelectedFeatureIds,
    id,
    isEditMode
  );

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();
    // If no changes were detected, return current formData to allow navigation
    if (!savedData) {
      return { ...formData, selectedFeatureIds };
    }
    return savedData;
  }, [handleSubmit, formData, selectedFeatureIds]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/class-of-services/feature-categories',
    newPath: '/pbx/class-of-services/feature-categories/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => `/pbx/class-of-services/feature-categories/${savedData?.uuid || savedData?.id || id}`,
    getFormData: () => ({ ...formData, selectedFeatureIds }),
    storageKey: 'featureCategories:saveAndNewData',
  });

  const loadQuickList = useCallback(async () => {
    setIsLoadingQuickList(true);
    try {
      const categories = await fetchFeatureCategoriesForQuickList(t);
      setQuickListCategories(categories);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('featureCategories.messages.loadError');
      showError(message);
      setQuickListCategories([]);
    } finally {
      setIsLoadingQuickList(false);
    }
  }, [showError, t]);

  useEffect(() => {
    if (showQuickList) {
      loadQuickList();
    }
  }, [loadQuickList, showQuickList]);

  const handleDeleteClick = useCallback(() => {
    if (isEditMode && id) {
      setShowDeleteModal(true);
    }
  }, [id, isEditMode]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteFeatureCategory(id, t);

      showSuccess(t('featureCategories.messages.deleteSuccess'));
      navigate('/pbx/class-of-services/feature-categories');
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t('featureCategories.messages.deleteError');
      showError(message);
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [id, isEditMode, navigate, showError, showSuccess, t]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToFeatureCategoryDuplicate(navigate, id);
  }, [isEditMode, id, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/class-of-services/feature-categories/new');
  }, [navigate]);

  const handleQuickListSelect = useCallback(
    async (categoryId: string | number) => {
      try {
        const category = await fetchFeatureCategoryById(String(categoryId), t);

        const members = Array.isArray(category.members)
          ? category.members.map((member) => member.featureCodeId)
          : [];

        const normalisedForm = {
          name: (category.name ?? '').trim(),
          description: (category.description ?? '').trim(),
          enabled: category.enabled ?? true,
        };

        const memberSet = new Set<number>(members);

        handleChange('name', normalisedForm.name);
        handleChange('description', normalisedForm.description);
        handleChange('enabled', normalisedForm.enabled);
        moveToEnabled(Array.from(memberSet));

        navigate(`/pbx/class-of-services/feature-categories/${categoryId}`, { replace: true });
        showSuccess(t('featureCategories.messages.loadSuccess'));
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : t('featureCategories.messages.loadError');
        showError(message);
      } finally {
        setShowQuickList(false);
        setQuickListSearch('');
      }
    },
    [handleChange, moveToEnabled, navigate, showError, showSuccess, t]
  );

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query) {
      return quickListCategories;
    }
    return quickListCategories.filter(
      (category) =>
        category.name?.toLowerCase().includes(query) ||
        category.description?.toLowerCase().includes(query)
    );
  }, [quickListCategories, quickListSearch]);

  if (isLoading || !formData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6"><ModuleFormHeader
        icon={<Layers className="h-4 w-4 text-primary" />}
        title={
          isEditMode
            ? `${t('featureCategories.editCategory')} - ${formData.name || ''}`
            : t('featureCategories.createCategory')
        }
        backButtonText={t('common.list', 'List')}
        onBack={() => navigate('/pbx/class-of-services/feature-categories')}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('featureCategories.actions.quickList'),
            onClick: () => setShowQuickList(true),
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('featureCategories.actions.duplicate'),
                onClick: handleDuplicateFromForm,
              },
            ]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('featureCategories.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <div className="mt-8">
        <form className="space-y-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
              {t('featureCategories.sections.basicInformation', {
                defaultValue: t('common.basicInformation'),
              })}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
              <FormField
                id="name"
                label={t('featureCategories.fields.name.label')}
                tooltip={t('featureCategories.fields.name.tooltip')}
                required
              >
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(event) => handleChange('name', event.target.value)}
                  placeholder={t('featureCategories.fields.name.placeholder')}
                  className={errors.name ? 'border-red-500' : ''}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </FormField>

              <FormField
                id="description"
                label={t('featureCategories.fields.description.label')}
                tooltip={t('featureCategories.fields.description.tooltip')}
              >
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(event) => handleChange('description', event.target.value)}
                  placeholder={t('featureCategories.fields.description.placeholder')}
                />
              </FormField>

              <FormField
                id="enabled"
                label={t('featureCategories.fields.enabled.label')}
                tooltip={t('featureCategories.fields.enabled.tooltip')}
              >
                <Toggle
                  id="enabled"
                  value={formData.enabled}
                  onChange={(value) => handleChange('enabled', value)}
                />
              </FormField>
            </div>
          </div>

          <FeatureCodeShuttle
            availableFeatures={availableFeatures}
            selectedFeatureIds={selectedFeatureIds}
            onMoveToEnabled={moveToEnabled}
            onMoveToAvailable={moveToAvailable}
          />
        </form>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? handleDeleteClick : undefined}
        isSaving={isSaving}
        isEditMode={isEditMode}
        useSaveDropdown={true}
        saveText={t('featureCategories.actions.save')}
        cancelText={t('featureCategories.actions.cancel')}
        deleteText={t('common.delete')}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('featureCategories.messages.confirmDeleteTitle', {
          defaultValue: t('featureCategories.deleteCategory'),
        })}
        message={t('featureCategories.messages.confirmDeleteMessage', {
          name: formData.name || t('featureCategories.categoryName'),
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('featureCategories.quickListTitle')}
        items={filteredQuickList}
        isLoadingInitial={isLoadingQuickList && quickListCategories.length === 0}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(category) => handleQuickListSelect(category.uuid || category.id)}
        renderItem={(category) => (
          <button
            type="button"
            onClick={() => handleQuickListSelect(category.uuid || category.id)}
            className="w-full p-3 text-left border border-border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-1 bg-primary/10 rounded-md">
                <Layers className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-foreground truncate">{category.name}</div>
                {category.description && (
                  <div className="text-sm text-muted-foreground truncate">
                    {category.description}
                  </div>
                )}
              </div>
              <div className="text-sm text-muted-foreground">
                {category._count?.members ?? 0} {t('featureCategories.featureCount')}
              </div>
            </div>
          </button>
        )}
        searchPlaceholder={t('featureCategories.quickListSearchPlaceholder')}
        emptyMessage={t('featureCategories.quickListEmpty')}
        headerIcon={<Layers className="h-5 w-5" />}
        minSearchChars={0}
      />
    </div>
  );
}
